import { Check, Download, Edit3, Eye, Loader2, Plus, Search, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { ConfirmDialog } from "@shared/components/ConfirmDialog";
import { errMsg } from "@shared/api/errors";
import { useAuthStore } from "@shared/store/auth";
import {
  fetchCommercialValuationPdf,
  saveCommercialValuationPdf,
  useApproveCommercialValuation,
  useCommercialFormOptions,
  useCommercialValuations,
  useDeleteCommercialValuation,
  useSaveCommercialValuation,
} from "../api";
import { CommercialHeader } from "../components/CommercialHeader";
import { useCommercialStore } from "../store";
import type { CommercialValuation, CommercialValuationForm } from "../types";

const EMPTY_FORM: CommercialValuationForm = {
  cliente_id: null,
  razon_social: "",
  cif: "",
  concepto: "",
  descripcion: "",
  horas_auxiliar: 0,
  horas_supervisor: 0,
  horas_directivo: 0,
  costes_extra_unicos: 0,
  margen_pct: 0,
  licencia_id: null,
  numero_centros: 1,
};

const eur = new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" });
const date = new Intl.DateTimeFormat("es-ES", { dateStyle: "short" });

type ConfirmState =
  | { type: "edit"; valuation: CommercialValuation }
  | { type: "delete"; valuation: CommercialValuation }
  | { type: "approve"; valuation: CommercialValuation }
  | null;

type PdfPreviewState = {
  valuation: CommercialValuation;
  blob: Blob;
  filename: string;
  url: string;
} | null;

export function CommercialValuationsPage() {
  const { emisorId } = useCommercialStore();
  const permissions = useAuthStore((s) => s.me?.permissions ?? []);
  const canManage = permissions.includes("manage-commercial-valuations");
  const canApprove = permissions.includes("approve-commercial-valuations");

  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const filters = useMemo(() => ({ q: q || undefined, estado: estado || undefined, desde: desde || undefined, hasta: hasta || undefined }), [q, estado, desde, hasta]);

  const { data: valuations = [], isLoading } = useCommercialValuations(emisorId, filters);
  const { data: options } = useCommercialFormOptions(emisorId);
  const saveMut = useSaveCommercialValuation();
  const deleteMut = useDeleteCommercialValuation();
  const approveMut = useApproveCommercialValuation();
  const actionPending = saveMut.isPending || deleteMut.isPending || approveMut.isPending;

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CommercialValuation | null>(null);
  const [form, setForm] = useState<CommercialValuationForm>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [pdfGeneratingId, setPdfGeneratingId] = useState<number | null>(null);
  const [pdfPreview, setPdfPreview] = useState<PdfPreviewState>(null);

  useEffect(() => {
    return () => {
      if (pdfPreview?.url) {
        URL.revokeObjectURL(pdfPreview.url);
      }
    };
  }, [pdfPreview]);

  const profilePrice = (perfil: "auxiliar" | "supervisor" | "directivo") =>
    options?.perfiles.find((p) => p.perfil === perfil)?.total ?? 0;

  const preview = useMemo(() => {
    const aux = profilePrice("auxiliar") * Number(form.horas_auxiliar || 0);
    const sup = profilePrice("supervisor") * Number(form.horas_supervisor || 0);
    const dir = profilePrice("directivo") * Number(form.horas_directivo || 0);
    const subtotal = aux + sup + dir + Number(form.costes_extra_unicos || 0);
    const beneficio = subtotal * (Number(form.margen_pct || 0) / 100);
    const conBeneficio = subtotal + beneficio;
    const impuesto = conBeneficio * ((options?.configuracion.impuesto_pct ?? 23) / 100);
    const licenciaPct = options?.licencias.find((l) => l.id === form.licencia_id)?.porcentaje ?? 0;
    const licencia = conBeneficio * (licenciaPct / 100);
    const total = conBeneficio + impuesto + licencia;
    return { aux, sup, dir, subtotal, beneficio, impuesto, licencia, total, porCentro: total / Math.max(1, Number(form.numero_centros || 1)) };
  }, [form, options]);

  function openCreate() {
    if (actionPending) return;
    setEditing(null);
    setForm(EMPTY_FORM);
    setError(null);
    setModalOpen(true);
  }

  function openEdit(item: CommercialValuation) {
    if (actionPending) return;
    setEditing(item);
    setForm({
      cliente_id: item.cliente_id,
      razon_social: item.razon_social,
      cif: item.cif ?? "",
      concepto: item.concepto,
      descripcion: item.descripcion ?? "",
      horas_auxiliar: item.horas_auxiliar,
      horas_supervisor: item.horas_supervisor,
      horas_directivo: item.horas_directivo,
      costes_extra_unicos: item.costes_extra_unicos,
      margen_pct: item.margen_pct,
      licencia_id: item.licencia_id,
      numero_centros: item.numero_centros,
    });
    setError(null);
    setModalOpen(true);
  }

  async function persist() {
    if (!emisorId || saveMut.isPending) return;
    setError(null);
    try {
      await saveMut.mutateAsync({ emisorId, id: editing?.id, payload: form });
      setModalOpen(false);
      setEditing(null);
    } catch (e) {
      setError(errMsg(e, "No se pudo guardar la valoración."));
    }
  }

  async function openPdfPreview(item: CommercialValuation) {
    if (!emisorId || pdfGeneratingId !== null) return;
    setError(null);
    setPdfGeneratingId(item.id);
    try {
      const pdf = await fetchCommercialValuationPdf(emisorId, item.id);
      setPdfPreview({
        valuation: item,
        blob: pdf.blob,
        filename: pdf.filename,
        url: URL.createObjectURL(pdf.blob),
      });
    } catch (e) {
      setError(errMsg(e, "No se pudo generar la vista preliminar del presupuesto PDF."));
    } finally {
      setPdfGeneratingId(null);
    }
  }

  function downloadPreviewPdf() {
    if (!pdfPreview) return;
    saveCommercialValuationPdf({ blob: pdfPreview.blob, filename: pdfPreview.filename });
  }

  function submitForm(e: React.FormEvent) {
    e.preventDefault();
    if (editing) {
      setConfirm({ type: "edit", valuation: editing });
      return;
    }
    void persist();
  }

  async function runConfirm() {
    if (!confirm || !emisorId) return;
    try {
      if (confirm.type === "edit") await persist();
      if (confirm.type === "delete") await deleteMut.mutateAsync({ emisorId, id: confirm.valuation.id });
      if (confirm.type === "approve") await approveMut.mutateAsync({ emisorId, id: confirm.valuation.id });
    } catch (e) {
      setError(errMsg(e, "No se pudo completar la operación."));
    }
  }

  const confirmCopy = confirm?.type === "delete"
    ? { title: "Eliminar valoración", message: `¿Eliminar definitivamente la valoración #${confirm.valuation.id} de ${confirm.valuation.razon_social}?`, label: "Eliminar", destructive: true }
    : confirm?.type === "approve"
      ? { title: "Aprobar valoración", message: `¿Confirmas la aprobación de la valoración #${confirm.valuation.id}?`, label: "Aprobar", destructive: false }
      : { title: "Guardar edición", message: "¿Confirmas los cambios realizados en esta valoración?", label: "Guardar cambios", destructive: false };

  return (
    <div className="space-y-6 p-6 lg:p-8">
      <CommercialHeader title="Valoraciones" subtitle="Alta, edición, aprobación y seguimiento de presupuestos comerciales." />

      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm lg:flex-row lg:items-end">
        <label className="flex-1 text-xs font-black uppercase tracking-wide text-slate-500">Buscar
          <div className="relative mt-1"><Search className="absolute left-3 top-2.5 text-slate-400" size={17} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cliente, CIF o concepto" className="w-full rounded-xl border border-slate-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-emerald-500" /></div>
        </label>
        <label className="text-xs font-black uppercase tracking-wide text-slate-500">Estado
          <select value={estado} onChange={(e) => setEstado(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold outline-none"><option value="">Todos</option><option value="borrador">Borrador</option><option value="aprobado">Aprobado</option></select>
        </label>
        <label className="text-xs font-black uppercase tracking-wide text-slate-500">Desde<input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="mt-1 block rounded-xl border border-slate-200 px-3 py-2.5 text-sm" /></label>
        <label className="text-xs font-black uppercase tracking-wide text-slate-500">Hasta<input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="mt-1 block rounded-xl border border-slate-200 px-3 py-2.5 text-sm" /></label>
        {canManage && <button type="button" onClick={openCreate} disabled={!emisorId || actionPending} className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-700 px-4 py-2.5 text-sm font-black text-white hover:bg-emerald-800 disabled:cursor-wait disabled:opacity-50"><Plus size={17} />Nueva valoración</button>}
      </div>

      {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">{error}</div>}
      {(deleteMut.isPending || approveMut.isPending) && <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800"><Loader2 className="animate-spin" size={17} />{deleteMut.isPending ? "Eliminando valoración…" : "Aprobando valoración…"}</div>}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-[1100px] w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">Acciones</th><th className="px-4 py-3">Nº</th><th className="px-4 py-3">Fecha</th><th className="px-4 py-3">Cliente</th><th className="px-4 py-3">CIF</th><th className="px-4 py-3">Concepto</th><th className="px-4 py-3 text-right">Horas A/S/D</th><th className="px-4 py-3 text-right">Margen</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3">Estado</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-500">Cargando valoraciones…</td></tr> : valuations.length ? valuations.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50/70">
                  <td className="px-4 py-3"><div className="flex gap-1">
                    {canManage && <button type="button" onClick={() => openEdit(item)} disabled={actionPending} title="Editar" className="rounded-lg p-2 text-blue-700 hover:bg-blue-50 disabled:cursor-wait disabled:opacity-40"><Edit3 size={16} /></button>}
                    {canApprove && item.estado !== "aprobado" && <button type="button" onClick={() => setConfirm({ type: "approve", valuation: item })} disabled={actionPending} title="Aprobar" className="rounded-lg p-2 text-emerald-700 hover:bg-emerald-50 disabled:cursor-wait disabled:opacity-40">{approveMut.isPending && approveMut.variables?.id === item.id ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}</button>}
                    <button type="button" onClick={() => void openPdfPreview(item)} disabled={pdfGeneratingId !== null || actionPending} title="Vista preliminar del presupuesto PDF" className="rounded-lg p-2 text-slate-700 hover:bg-slate-100 disabled:cursor-wait disabled:opacity-50">{pdfGeneratingId === item.id ? <Loader2 className="animate-spin" size={16} /> : <Eye size={16} />}</button>
                    {canManage && <button type="button" onClick={() => setConfirm({ type: "delete", valuation: item })} disabled={actionPending} title="Eliminar" className="rounded-lg p-2 text-rose-700 hover:bg-rose-50 disabled:cursor-wait disabled:opacity-40">{deleteMut.isPending && deleteMut.variables?.id === item.id ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} />}</button>}
                  </div></td>
                  <td className="px-4 py-3 font-bold">#{item.id}</td><td className="px-4 py-3 text-slate-500">{item.created_at ? date.format(new Date(item.created_at)) : "—"}</td><td className="px-4 py-3 font-semibold text-slate-800">{item.razon_social}</td><td className="px-4 py-3 text-slate-500">{item.cif || "—"}</td><td className="px-4 py-3 text-slate-700">{item.concepto}</td><td className="px-4 py-3 text-right text-slate-600">{item.horas_auxiliar}/{item.horas_supervisor}/{item.horas_directivo}</td><td className="px-4 py-3 text-right">{item.margen_pct.toFixed(2)}%</td><td className="px-4 py-3 text-right font-black">{eur.format(item.total)}</td><td className="px-4 py-3"><span className={item.estado === "aprobado" ? "rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-black text-emerald-700" : "rounded-full bg-amber-50 px-2.5 py-1 text-xs font-black text-amber-700"}>{item.estado === "aprobado" ? "Aprobado" : "Borrador"}</span></td>
                </tr>
              )) : <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-500">No hay valoraciones con los filtros seleccionados.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {pdfPreview && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/55 p-4" onMouseDown={(e) => e.target === e.currentTarget && setPdfPreview(null)}>
          <div role="dialog" aria-modal="true" aria-label="Vista preliminar del presupuesto PDF" className="flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4 sm:px-6">
              <div className="min-w-0">
                <h2 className="text-lg font-black text-slate-900 sm:text-xl">Vista preliminar del presupuesto</h2>
                <p className="mt-1 truncate text-sm font-semibold text-slate-500">Valoración #{pdfPreview.valuation.id} · {pdfPreview.valuation.razon_social} · {pdfPreview.filename}</p>
              </div>
              <button type="button" onClick={() => setPdfPreview(null)} title="Cerrar vista preliminar" className="ml-4 rounded-xl p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"><X size={20} /></button>
            </div>

            <div className="min-h-0 flex-1 bg-slate-100 p-3 sm:p-5">
              <iframe
                src={pdfPreview.url}
                title={`Presupuesto ${pdfPreview.filename}`}
                className="h-full w-full rounded-2xl border border-slate-200 bg-white shadow-sm"
              />
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-white px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
              <button type="button" onClick={() => setPdfPreview(null)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-black text-slate-700 hover:bg-slate-50">Cerrar</button>
              <button type="button" onClick={downloadPreviewPdf} className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-700 px-4 py-2.5 text-sm font-black text-white hover:bg-emerald-800"><Download size={17} />Descargar PDF</button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4" onMouseDown={(e) => e.target === e.currentTarget && !saveMut.isPending && setModalOpen(false)}>
          <div role="dialog" aria-modal="true" className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-3xl bg-white shadow-2xl">
            <form onSubmit={submitForm}>
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white px-6 py-4"><div><h2 className="text-xl font-black text-slate-900">{editing ? `Editar valoración #${editing.id}` : "Nueva valoración"}</h2><p className="text-sm text-slate-500">El servidor recalculará todos los importes al guardar.</p></div><button type="button" onClick={() => setModalOpen(false)} disabled={saveMut.isPending} className="rounded-lg p-2 hover:bg-slate-100 disabled:cursor-wait disabled:opacity-40"><X size={20} /></button></div>
              <div className="grid gap-5 p-6 lg:grid-cols-[1.25fr_.75fr]">
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Cliente existente"><select value={form.cliente_id ?? ""} onChange={(e) => { const id = Number(e.target.value) || null; const client = options?.clientes.find((c) => c.id === id); setForm((f) => ({ ...f, cliente_id: id, razon_social: client?.nombre ?? f.razon_social, cif: client?.cif ?? f.cif })); }} className={inputClass}><option value="">Cliente manual / potencial</option>{options?.clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}</select></Field>
                    <Field label="CIF / NIF"><input value={form.cif} onChange={(e) => setForm((f) => ({ ...f, cif: e.target.value }))} className={inputClass} /></Field>
                    <Field label="Razón social"><input required={!form.cliente_id} value={form.razon_social} onChange={(e) => setForm((f) => ({ ...f, razon_social: e.target.value }))} className={inputClass} /></Field>
                    <Field label="Concepto"><input required value={form.concepto} onChange={(e) => setForm((f) => ({ ...f, concepto: e.target.value }))} className={inputClass} /></Field>
                  </div>
                  <Field label="Descripción"><textarea rows={3} value={form.descripcion} onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))} className={inputClass} /></Field>

                  <div className="rounded-2xl border border-slate-200 p-4"><h3 className="font-black text-slate-900">Horas necesarias</h3><div className="mt-3 grid gap-4 md:grid-cols-3">
                    <NumberField label={`Auxiliar · ${eur.format(profilePrice("auxiliar"))}/h`} value={form.horas_auxiliar} onChange={(v) => setForm((f) => ({ ...f, horas_auxiliar: v }))} />
                    <NumberField label={`Supervisor · ${eur.format(profilePrice("supervisor"))}/h`} value={form.horas_supervisor} onChange={(v) => setForm((f) => ({ ...f, horas_supervisor: v }))} />
                    <NumberField label={`Directivo · ${eur.format(profilePrice("directivo"))}/h`} value={form.horas_directivo} onChange={(v) => setForm((f) => ({ ...f, horas_directivo: v }))} />
                  </div></div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <NumberField label="Costes extra únicos (€)" value={form.costes_extra_unicos} onChange={(v) => setForm((f) => ({ ...f, costes_extra_unicos: v }))} step="0.01" />
                    <NumberField label="Beneficio (%)" value={form.margen_pct} onChange={(v) => setForm((f) => ({ ...f, margen_pct: v }))} step="0.01" />
                    <Field label="Licencia"><select value={form.licencia_id ?? ""} onChange={(e) => setForm((f) => ({ ...f, licencia_id: Number(e.target.value) || null }))} className={inputClass}><option value="">Sin licencia</option>{options?.licencias.map((l) => <option key={l.id} value={l.id}>{l.nombre} ({l.porcentaje}%)</option>)}</select></Field>
                    <NumberField label="Número de centros" value={form.numero_centros} onChange={(v) => setForm((f) => ({ ...f, numero_centros: Math.max(1, Math.trunc(v || 1)) }))} step="1" min="1" />
                  </div>
                  {error && <div className="rounded-xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">{error}</div>}
                </div>

                <aside className="h-fit rounded-2xl border border-emerald-100 bg-emerald-50/60 p-5"><h3 className="font-black text-emerald-950">Previsualización</h3><div className="mt-4 space-y-2 text-sm">
                  <PreviewRow label="Auxiliar" value={preview.aux} /><PreviewRow label="Supervisor" value={preview.sup} /><PreviewRow label="Directivo" value={preview.dir} /><PreviewRow label="Subtotal" value={preview.subtotal} strong /><PreviewRow label={`Beneficio (${form.margen_pct || 0}%)`} value={preview.beneficio} /><PreviewRow label={`Impuestos (${options?.configuracion.impuesto_pct ?? 23}%)`} value={preview.impuesto} /><PreviewRow label="Licencia" value={preview.licencia} /><div className="my-3 border-t border-emerald-200" /><PreviewRow label="Venta neta" value={preview.total} strong /><PreviewRow label="Total por centro" value={preview.porCentro} strong /></div></aside>
              </div>
              <div className="sticky bottom-0 flex justify-end gap-2 border-t border-slate-100 bg-white px-6 py-4"><button type="button" onClick={() => setModalOpen(false)} disabled={saveMut.isPending} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-black text-slate-700 hover:bg-slate-50 disabled:cursor-wait disabled:opacity-50">Cancelar</button><button type="submit" disabled={saveMut.isPending} className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-black text-white hover:bg-emerald-800 disabled:cursor-wait disabled:opacity-50">{saveMut.isPending && <Loader2 className="animate-spin" size={16} />}{saveMut.isPending ? "Guardando…" : editing ? "Guardar cambios" : "Crear valoración"}</button></div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog open={confirm !== null} title={confirmCopy.title} message={confirmCopy.message} confirmLabel={confirmCopy.label} destructive={confirmCopy.destructive} onConfirm={() => void runConfirm()} onClose={() => setConfirm(null)} />
    </div>
  );
}

const inputClass = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block text-xs font-black uppercase tracking-wide text-slate-500">{label}<div className="mt-1">{children}</div></label>;
}

function NumberField({ label, value, onChange, step = "0.5", min = "0" }: { label: string; value: number; onChange: (value: number) => void; step?: string; min?: string }) {
  return <Field label={label}><input type="number" min={min} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className={inputClass} /></Field>;
}

function PreviewRow({ label, value, strong = false }: { label: string; value: number; strong?: boolean }) {
  return <div className={`flex items-center justify-between gap-4 ${strong ? "font-black text-slate-900" : "font-semibold text-slate-600"}`}><span>{label}</span><span>{eur.format(value)}</span></div>;
}
